// Model files run to tens of megabytes, and Chrome refuses to keep a single HTTP
// cache entry above ~40 MiB (measured: 41943040 bytes stores, one byte more does
// not). Production outputs are well past that, so the browser re-downloads them
// on every switch no matter what the response headers say. Cache Storage has no
// such per-entry limit, so the studio keeps its own copies here instead.
const CACHE_NAME = 'cgt-studio-models-v1'

// The most recently opened models, which is what the cache is meant to hold. The
// prefetch fills spare slots with sibling texture variations, so the two writers
// share this budget.
export const MODEL_CACHE_MAX_ENTRIES = 5

// Entry count alone makes the footprint a function of luck: observed models run
// from 8 MB to 64 MB, an 8x swing for the same five entries. The byte budget is
// what actually bounds what we leave on someone's disk, and whichever limit
// binds first wins. Storage here is best-effort (navigator.storage.persisted()
// is false), so staying modest also makes us a less likely eviction target.
const MAX_BYTES = 400 * 1024 * 1024

// Cache Storage enforces none of the access control the asset URL carries: a
// stored blob stays readable through cache.match long after its signature would
// have stopped granting access. Since the key deliberately drops the signature
// (see cacheKey), an entry has to carry its own expiry or a model whose access
// was revoked would keep opening from disk. Falls back to the signing window
// SignedAssetUrl uses when the URL states no expiry of its own.
const FALLBACK_MAX_AGE_MS = 6 * 60 * 60 * 1000

// One record per entry, least-recently-used first: `{ key, bytes, expiresAt }`.
// Recency lives outside the cache because re-inserting an entry to refresh its
// position would mean rewriting tens of megabytes to disk on every hit; size and
// expiry ride along because Cache Storage exposes neither.
const RECORDS_KEY = 'cgtStudioModelCacheOrder'

// Every failure path here is a cache miss, never an error the caller has to
// handle: storage can be disabled, evicted under pressure, or over quota, and in
// all of those cases the right answer is to fall back to the network.
export const isModelCacheSupported = () => (
  typeof window !== 'undefined' && 'caches' in window && window.isSecureContext
)

// The signed asset URL rotates its signature every 6 hours, so the query string
// has to stay out of the key - otherwise each rotation would orphan the copy we
// already hold and re-download the whole file.
const cacheKey = (url) => {
  const parsed = new URL(url, window.location.href)

  return `${parsed.origin}${parsed.pathname}`
}

// CloudFront states an absolute deadline; a presigned S3 URL states an issue
// time plus a duration. Anything else (a Rails blob redirect, say) states none,
// and takes the fallback window.
const expiryFromUrl = (url) => {
  try {
    const { searchParams } = new URL(url, window.location.href)

    const absolute = Number(searchParams.get('Expires'))
    if (Number.isFinite(absolute) && absolute > 0) {
      return absolute * 1000
    }

    const issued = searchParams.get('X-Amz-Date')
    const lifetime = Number(searchParams.get('X-Amz-Expires'))
    if (issued && Number.isFinite(lifetime) && lifetime > 0) {
      const iso = issued.replace(
        /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
        '$1-$2-$3T$4:$5:$6Z',
      )
      const issuedAt = Date.parse(iso)
      if (Number.isFinite(issuedAt)) {
        return issuedAt + lifetime * 1000
      }
    }

    return Date.now() + FALLBACK_MAX_AGE_MS
  } catch {
    return Date.now() + FALLBACK_MAX_AGE_MS
  }
}

const readRecords = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(RECORDS_KEY))

    return Array.isArray(stored) ? stored.filter(record => record?.key) : []
  } catch {
    return []
  }
}

const writeRecords = (records) => {
  try {
    window.localStorage.setItem(RECORDS_KEY, JSON.stringify(records))
  } catch {
    // Private mode or a full quota. Entries then look unknown to eviction, which
    // drops them first - conservative, and never serves them past their expiry.
  }
}

const recordFor = key => readRecords().find(record => record.key === key)

const rememberUse = (key, patch = {}) => {
  const existing = recordFor(key)

  writeRecords([
    ...readRecords().filter(record => record.key !== key),
    { key, bytes: 0, ...existing, ...patch },
  ])
}

const forgetKeys = (keys) => {
  const dropped = new Set(keys)

  writeRecords(readRecords().filter(record => !dropped.has(record.key)))
}

const isExpired = key => (recordFor(key)?.expiresAt ?? 0) <= Date.now()

// Size has to come from somewhere the byte budget can trust. content-length is
// the cheap answer and our CDN sends it; when it is missing the body is measured
// once at write time rather than counted as zero, which would quietly excuse the
// entry from the budget forever.
const measureBytes = async (cache, key, response) => {
  const stated = Number(response.headers?.get('content-length'))
  if (Number.isFinite(stated) && stated > 0) {
    return stated
  }

  try {
    const stored = await cache.match(key)

    return (await stored.blob()).size
  } catch {
    return 0
  }
}

// Drops an entry and its record together, so a stale key can never linger in
// either half.
const discard = async (cache, keys) => {
  if (keys.length === 0) {
    return
  }

  await Promise.all(keys.map(key => cache.delete(key)))
  forgetKeys(keys)
}

// Oldest use first, so what goes is what the user is least likely to come back
// to. Sizes come from the records rather than a match-per-entry. An entry with no
// record is unknown to us and goes first. Never drops the most recent entry -
// that is the one just stored, and evicting it would mean paying for a download
// and keeping nothing.
const evictOverflow = async (cache) => {
  const keys = (await cache.keys()).map(request => request.url)
  const records = readRecords()
  const rank = new Map(records.map((record, index) => [record.key, index]))
  const sizes = new Map(records.map(record => [record.key, record.bytes ?? 0]))

  const byLeastRecent = [...keys].sort((a, b) => (
    (rank.get(a) ?? -1) - (rank.get(b) ?? -1)
  ))
  const totalBytes = byLeastRecent.reduce((sum, key) => sum + (sizes.get(key) ?? 0), 0)

  const { doomed } = byLeastRecent.slice(0, -1).reduce((state, key) => {
    const overEntries = keys.length - state.doomed.length > MODEL_CACHE_MAX_ENTRIES
    const overBytes = state.bytes > MAX_BYTES

    if (!overEntries && !overBytes) {
      return state
    }

    return { doomed: [...state.doomed, key], bytes: state.bytes - (sizes.get(key) ?? 0) }
  }, { doomed: [], bytes: totalBytes })

  await discard(cache, doomed)
}

/**
 * Whether a model file is stored and still within its access window, so a
 * prefetch can skip it and the viewer can tell a cache read from a download.
 * Survives page loads, which is the point: the in-memory guards do not. A stale
 * entry is deleted here rather than reported, so the next load refreshes it.
 */
export const hasModel = async (url) => {
  if (!isModelCacheSupported()) {
    return false
  }

  try {
    const cache = await caches.open(CACHE_NAME)
    const key = cacheKey(url)

    if (!await cache.match(key)) {
      return false
    }

    if (isExpired(key)) {
      await discard(cache, [key])

      return false
    }

    return true
  } catch {
    return false
  }
}

/**
 * Marks a stored model as just used, so eviction drops what has gone cold rather
 * than what arrived first. Call on every cache hit.
 */
export const touchModel = (url) => {
  if (!isModelCacheSupported()) {
    return
  }

  try {
    rememberUse(cacheKey(url))
  } catch {
    // A malformed URL cannot be ranked; insertion order still applies.
  }
}

/**
 * Stores a fetched model response. Consumes the response body, so the caller
 * must not have read it.
 *
 * @returns {Promise<boolean>} whether it was stored
 */
export const putModel = async (url, response) => {
  if (!isModelCacheSupported() || !response?.ok) {
    return false
  }

  try {
    const cache = await caches.open(CACHE_NAME)
    const key = cacheKey(url)
    await cache.put(key, response)
    rememberUse(key, { bytes: await measureBytes(cache, key, response), expiresAt: expiryFromUrl(url) })
    await evictOverflow(cache)

    return true
  } catch {
    // Most likely QuotaExceededError, or an opaque response that cannot be
    // stored. Either way the model stays loadable from the network.
    return false
  }
}

/**
 * A blob URL for a stored model, or null when it is not held or has outlived its
 * access window. The caller owns the returned URL and MUST revoke it once the
 * viewer has finished loading, or the blob stays resident for the life of the
 * document.
 */
export const getModelBlobUrl = async (url) => {
  if (!isModelCacheSupported()) {
    return null
  }

  try {
    const cache = await caches.open(CACHE_NAME)
    const key = cacheKey(url)
    const hit = await cache.match(key)

    if (!hit) {
      return null
    }

    // The signature that authorised this download has lapsed, so serving it now
    // would hand back a model the URL no longer grants access to.
    if (isExpired(key)) {
      await discard(cache, [key])

      return null
    }

    return URL.createObjectURL(await hit.blob())
  } catch {
    return null
  }
}
